// berufRentner.ts — der Beruf-Block für Rentner, plus die Frage nach einer
// weiteren Tätigkeit.
//
// „Rentner" ist ein eigener Angestelltenstatus (`Contact.jobStatus`). Gespeichert
// wird der Katalogwert „Rentner/in" aus `ContactBerufsstatus` — denselben Wert
// tragen die rund 500 importierten Rentner schon. So erscheinen sie in der
// Maske als „Rentner" statt als leeres Feld, und die Anlage schreibt keinen
// zweiten Begriff für dieselbe Sache.
//
// Ein Rentner hat keinen Arbeitgeber. Viele arbeiten aber weiter — als
// Minijobber beim alten Arbeitgeber oder selbstständig. Das hält
// `Contact.weitereTaetigkeit` fest:
//   NULL             → keine weitere Tätigkeit
//   'angestellt'     → der Arbeitgeber steht in der Arbeitgeber-Beziehung
//                      (`employerContactId`, `ContactRelation.istHauptarbeitgeber`)
//   'selbststaendig' → die Tätigkeit steht als Freitext in `jobOccupation`
//
// Die `value` sind Speicherwerte und dürfen sich nicht ändern; die
// Beschriftungen dürfen es.

/** Speicherwert des Angestelltenstatus „Rentner" (Katalog `ContactBerufsstatus`). */
export const RENTNER_STATUS = "Rentner/in";

export type WeitereTaetigkeit = "angestellt" | "selbststaendig";

export const WEITERE_TAETIGKEIT_OPTIONEN: ReadonlyArray<{ value: WeitereTaetigkeit; label: string }> = [
  { value: "angestellt", label: "Angestellt" },
  { value: "selbststaendig", label: "Selbstständig" },
];

/** Vorgabe, sobald „ja, weitere Tätigkeit" gewählt wird. */
export const WEITERE_TAETIGKEIT_STANDARD: WeitereTaetigkeit = "angestellt";

/** Ist der Status „Rentner"? Toleriert die Schreibweise ohne „/in". */
export const istRentner = (status?: string | null): boolean =>
  /^rentner(\/in)?$/i.test(String(status ?? "").trim());

/** Beschriftung zu einem gespeicherten Wert — Unbekanntes bleibt sichtbar. */
export const weitereTaetigkeitLabel = (wert?: string | null): string => {
  const w = String(wert ?? "").trim();
  return WEITERE_TAETIGKEIT_OPTIONEN.find((o) => o.value === w)?.label ?? w;
};

/** Welche Felder zeigt der Beruf-Block? */
export interface BerufSichtbarkeit {
  /** Arbeitgeber-Auswahl (Firmenkontakt). */
  arbeitgeber: boolean;
  /** Freitext „Tätigkeit" des selbstständigen Rentners. */
  rentnerTaetigkeit: boolean;
  /** Ja/Nein-Frage „zusätzlich eine Tätigkeit?" samt „Weiterer Status". */
  rentnerFrage: boolean;
}

export function berufSichtbarkeit(status?: string | null, weitere?: string | null): BerufSichtbarkeit {
  if (!istRentner(status)) {
    return { arbeitgeber: true, rentnerTaetigkeit: false, rentnerFrage: false };
  }
  const w = String(weitere ?? "").trim();
  return {
    arbeitgeber: w === "angestellt",
    rentnerTaetigkeit: w === "selbststaendig",
    rentnerFrage: true,
  };
}

/**
 * Welche Felder beim Wechsel geleert werden müssen, damit nichts Verstecktes
 * gespeichert wird: ein Arbeitgeber, den die Maske nicht mehr zeigt, landete
 * sonst unsichtbar in der Beziehung.
 *
 * Liefert die zu leerenden Formularfelder; der Aufrufer setzt sie auf "".
 */
export function berufFelderZuLeeren(
  vorher: { status?: string | null; weitere?: string | null },
  nachher: { status?: string | null; weitere?: string | null },
): Array<"employer" | "position" | "weitereTaetigkeit" | "zusaetzlichSelbststaendig"> {
  const alt = berufSichtbarkeit(vorher.status, vorher.weitere);
  const neu = berufSichtbarkeit(nachher.status, nachher.weitere);
  const out: Array<"employer" | "position" | "weitereTaetigkeit" | "zusaetzlichSelbststaendig"> = [];
  if (alt.arbeitgeber && !neu.arbeitgeber) out.push("employer");
  // Die Tätigkeit (`position`) wandert zwischen „Was macht er als
  // Selbstständiger?" und „Tätigkeit" des Rentners; in den Rentner hinein oder
  // aus ihm heraus ist sie ein anderer Sachverhalt.
  if (istRentner(vorher.status) !== istRentner(nachher.status)) out.push("position");
  else if (alt.rentnerTaetigkeit && !neu.rentnerTaetigkeit) out.push("position");
  if (!istRentner(nachher.status) && String(nachher.weitere ?? "").trim()) out.push("weitereTaetigkeit");
  if (istRentner(nachher.status) && !istRentner(vorher.status)) out.push("zusaetzlichSelbststaendig");
  return out;
}
